import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Save, Upload, Eye, CheckCircle2, AlertCircle } from "lucide-react";
import { tplApi } from "@/lib/templateApi";
import ImageUpload from "@/components/ImageUpload";

const TEXT_FIELDS = [
  { key: "headline",    label: "Headline",    help: "The big text visitors see first.",                type: "text" },
  { key: "subheadline", label: "Subheadline", help: "A short sentence below the headline.",           type: "text" },
  { key: "cta_text",    label: "Button Text", help: "What the main button says (e.g. 'View Menu').",  type: "text" },
  { key: "cta_link",    label: "Button Link", help: "Where the button goes (e.g. '#menu' or a URL).", type: "text" },
];

export default function HeroEditor() {
  const { tenant, slug } = useOutletContext();
  const theme = tenant?.theme || {};
  const primary = theme.primary_color || "#1E3A5F";
  const font = theme.font || "Inter";

  const [form, setForm] = useState({ headline: "", subheadline: "", background_image: "", cta_text: "", cta_link: "" });
  const [publishedVersion, setPublishedVersion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const loadContent = useCallback(async () => {
    try {
      const res = await tplApi.getAdminContent(slug);
      const heroData = res.modules?.hero;
      if (heroData) {
        const src = heroData.draft || heroData.published;
        if (src) setForm(src.content_json);
        if (heroData.published) setPublishedVersion(heroData.published.content_json);
      }
    } catch {
      /* ignore */
    }
  }, [slug]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const showToast = (msg, type = "success", link = null) => {
    setToast({ msg, type, link });
    setTimeout(() => setToast(null), 5000);
  };

  const save = async (action) => {
    setSaving(true);
    try {
      await tplApi.saveContent(slug, "hero", form, action);
      showToast(action === "publish" ? "Published! Your live site is updated." : "Draft saved.", "success", action === "publish" ? `/t/${slug}` : null);
      if (action === "publish") setPublishedVersion({ ...form });
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const hasChanges = publishedVersion && JSON.stringify(form) !== JSON.stringify(publishedVersion);

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="hero-editor">
      {/* Editor */}
      <div className="lg:w-[440px] shrink-0 border-r border-white/[0.06] overflow-auto">
        <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Hero Section</h2>
            <p className="text-slate-500 text-xs mt-0.5">This is the first thing visitors see.</p>
          </div>
          {hasChanges && (
            <span className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded-full font-medium">
              Unsaved
            </span>
          )}
        </div>

        <div className="p-6 space-y-5">
          {TEXT_FIELDS.slice(0, 2).map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-300 block mb-1">{f.label}</label>
              <Input
                type={f.type}
                value={form[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                className="bg-[#1E293B] border-white/10 text-white placeholder:text-slate-600 h-10 rounded-lg focus:ring-orange-500"
                data-testid={`hero-field-${f.key}`}
              />
              <p className="text-[11px] text-slate-600 mt-1">{f.help}</p>
            </div>
          ))}

          <ImageUpload
            slug={slug}
            value={form.background_image}
            onChange={(url) => update("background_image", url)}
            label="Background Image"
            help="Upload a photo or paste a URL. Landscape images work best."
          />
          <div>
            <Input
              type="url"
              value={form.background_image || ""}
              onChange={(e) => update("background_image", e.target.value)}
              placeholder="Or paste an image URL..."
              className="bg-[#1E293B] border-white/10 text-white placeholder:text-slate-600 h-9 rounded-lg text-xs focus:ring-orange-500"
              data-testid="hero-field-background_image"
            />
          </div>

          {TEXT_FIELDS.slice(2).map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-300 block mb-1">{f.label}</label>
              <Input
                type={f.type}
                value={form[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                className="bg-[#1E293B] border-white/10 text-white placeholder:text-slate-600 h-10 rounded-lg focus:ring-orange-500"
                data-testid={`hero-field-${f.key}`}
              />
              <p className="text-[11px] text-slate-600 mt-1">{f.help}</p>
            </div>
          ))}
        </div>

        <div className="p-6 border-t border-white/[0.06] flex gap-3 sticky bottom-0 bg-[#0F172A]">
          <button
            onClick={() => save("draft")}
            disabled={saving}
            className="flex-1 bg-[#1E293B] border border-white/10 text-slate-300 hover:text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="save-draft-btn"
          >
            <Save size={15} /> Save Draft
          </button>
          <button
            onClick={() => save("publish")}
            disabled={saving}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="publish-btn"
          >
            <Upload size={15} /> Publish
          </button>
        </div>
      </div>

      {/* Live Preview */}
      <div className="flex-1 bg-[#0B1120] overflow-auto" data-testid="hero-preview-panel">
        <div className="p-4 border-b border-white/[0.06] flex items-center gap-2">
          <Eye size={14} className="text-slate-500" />
          <span className="text-xs text-slate-500 font-medium">Live Preview</span>
        </div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-white/[0.06]" style={{ maxWidth: 800 }}>
            {/* Mini browser chrome */}
            <div className="flex items-center px-3 py-2" style={{ background: "#1C1C1E" }}>
              <div className="flex gap-1 mr-3">
                <div className="w-2 h-2 rounded-full bg-[#FF5F56]" />
                <div className="w-2 h-2 rounded-full bg-[#FFBD2E]" />
                <div className="w-2 h-2 rounded-full bg-[#27C93F]" />
              </div>
              <div className="flex-1 bg-[#2C2C2E] rounded px-2 py-0.5">
                <span className="text-[10px] text-slate-500">{tenant?.name?.toLowerCase().replace(/\s+/g, "")}.com</span>
              </div>
            </div>

            {/* Preview content */}
            <div
              className="relative flex items-center justify-center text-center"
              style={{
                minHeight: 360,
                fontFamily: `'${font}', sans-serif`,
              }}
            >
              {form.background_image && (
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${form.background_image})` }}
                />
              )}
              <div
                className="absolute inset-0"
                style={{ background: form.background_image ? `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, ${primary}dd 100%)` : primary }}
              />
              <div className="relative z-10 px-8 py-12 max-w-lg">
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-3">
                  {form.headline || "Your Headline"}
                </h1>
                {form.subheadline && (
                  <p className="text-white/75 text-sm mb-5">{form.subheadline}</p>
                )}
                {form.cta_text && (
                  <span
                    className="inline-block px-5 py-2 rounded-full text-white text-sm font-semibold"
                    style={{ background: theme.secondary_color || "#F59E0B" }}
                  >
                    {form.cta_text}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium z-50 ${
            toast.type === "error"
              ? "bg-red-900/90 text-red-200 border border-red-800/50"
              : "bg-emerald-900/90 text-emerald-200 border border-emerald-800/50"
          }`}
          data-testid="toast"
        >
          {toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          {toast.msg}
          {toast.link && <a href={toast.link} target="_blank" rel="noopener noreferrer" className="underline ml-1">View site &rarr;</a>}
        </div>
      )}
    </div>
  );
}
